/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
*/

import React, { useMemo, useContext, createContext, useEffect } from 'react'
import { useGLTF, Merged } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three/webgpu'

const context = createContext()

// Detect mobile at module load time for GPU memory reductions
const IS_MOBILE = typeof window !== 'undefined' && window.innerWidth <= 768

// Mobile GLB has all textures resized to 1024² (vs 2048² desktop) — ~650 MB GPU savings on iOS
const GLB_PATH = IS_MOBILE ? '/models/saxhq-mobile.glb' : '/models/saxhq.glb'

export function Instances({ children, ...props }) {
  const { nodes } = useGLTF(GLB_PATH)
  const instances = useMemo(
    () => {
      const raw = {
        LSStructure: nodes.LS_Structure,
        Bucket: nodes.Bucket,
        LSStruts: nodes.LS_Struts,
        Tank: nodes.Tank050,
        Cargo: nodes.Cargo022,
        LSSmallPipes: nodes.LS_Small_Pipes,
        LSStairs: nodes.LS_Stairs,
        LSRails: nodes.LS_Rails,
        LSChonkyPipe: nodes.LS_Chonky_Pipe,
        LSFoundation: nodes.LS_Foundation,
        RAFBunker: nodes.RAF_Bunker000,
        RAFSmallBuilding: nodes.RAF_Small_Building000,
        HISLong: nodes.HIS_Long000,
      }
      // Filter out any nodes that didn't resolve in the loaded GLB
      const missing = Object.entries(raw).filter(([, v]) => v === undefined).map(([k]) => k)
      if (missing.length) console.warn('[saxhq] Missing GLB nodes for instances:', missing)
      return Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined))
    },
    [nodes]
  )
  return (
    <Merged meshes={instances} castShadow receiveShadow {...props}>
      {(instances) => <context.Provider value={instances} children={children} />}
    </Merged>
  )
}

// Rebuilds a GLB material as its three/webgpu NodeMaterial equivalent.
//
// useGLTF goes through drei -> three's GLTFLoader, which constructs MeshStandardMaterial from the
// plain `three` build regardless of what this file imports. Under a WebGPURenderer those are not
// the NodeMaterial classes the node pipeline expects, so they can draw nothing while still
// reporting healthy draw calls — no error, no warning, black screen.
//
// This is the mixed-build problem at the one layer that cannot be fixed by changing imports here.
function toNodeMaterial(src) {
  const Ctor = src.isMeshPhysicalMaterial
    ? THREE.MeshPhysicalNodeMaterial
    : THREE.MeshStandardNodeMaterial
  if (!Ctor) return src

  const next = new Ctor()
  // copy() carries colour, maps, roughness/metalness, transparency, side, etc.
  next.copy(src)
  next.name = src.name
  return next
}

export function Saxhq(props) {
  const instances = useContext(context)
  const { nodes, materials } = useGLTF(GLB_PATH)
  const { gl } = useThree()

  // Convert every GLB material to a NodeMaterial once, in place, before first render.
  // Mutating the shared `materials` object means all the material={materials.X} props below
  // pick up the converted versions without touching 200+ call sites.
  useMemo(() => {
    if (!gl?.isWebGPURenderer) return
    Object.keys(materials).forEach((key) => {
      const m = materials[key]
      if (m && !m.isNodeMaterial) materials[key] = toNodeMaterial(m)
    })
  }, [materials, gl])

  // Create detail noise texture — 256² on mobile (5× less VRAM, visually identical at 50× repeat)
  const detailTexture = useMemo(() => {
    const size = IS_MOBILE ? 256 : 1024
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    const imageData = ctx.createImageData(size, size)
    
    // Generate noise
    for (let i = 0; i < imageData.data.length; i += 4) {
      const noise = Math.random() * 255
      imageData.data[i] = noise
      imageData.data[i + 1] = noise
      imageData.data[i + 2] = noise
      imageData.data[i + 3] = 255
    }
    
    ctx.putImageData(imageData, 0, 0)
    
    const texture = new THREE.CanvasTexture(canvas)
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.RepeatWrapping
    texture.repeat.set(50, 50) // Tile the detail texture many times
    
    return texture
  }, [])
  
  // Add detail mapping shader to Land materials
  useEffect(() => {
    Object.keys(materials).forEach((key) => {
      if (key.startsWith('Land_')) {
        const material = materials[key]
        
        // Increase roughness
        material.roughness = 1.66
        
        // Enhance texture sharpness — cap anisotropy at 4 on mobile to reduce GPU sampling cost
        const anisotropy = IS_MOBILE ? 4 : 16
        if (material.map) {
          material.map.anisotropy = anisotropy
          material.map.needsUpdate = true
        }
        
        if (material.normalMap) {
          material.normalMap.anisotropy = anisotropy
          material.normalMap.needsUpdate = true
        }
        
        if (material.roughnessMap) {
          material.roughnessMap.anisotropy = anisotropy
          material.roughnessMap.needsUpdate = true
        }
        
        // Add detail texture using onBeforeCompile
        material.onBeforeCompile = (shader) => {
          shader.uniforms.detailMap = { value: detailTexture }
          shader.uniforms.detailScale = { value: 50.0 }
          shader.uniforms.detailStrength = { value: 0.3 }
          
          // Add detail map to fragment shader
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <map_fragment>',
            `
            #include <map_fragment>
            
            // Detail mapping
            vec2 detailUv = vMapUv * detailScale;
            vec4 detailColor = texture2D(detailMap, detailUv);
            float detailFactor = (detailColor.r - 0.5) * detailStrength;
            diffuseColor.rgb += detailFactor;
            `
          )
          
          // Add uniforms declaration
          shader.fragmentShader = 'uniform sampler2D detailMap;\nuniform float detailScale;\nuniform float detailStrength;\n' + shader.fragmentShader
        }
        
        material.needsUpdate = true
      }
    })
  }, [materials, detailTexture])
  
  return (
    <group {...props} dispose={null}>
      <group name="Scene">
        <group name="POI_HA1" position={[1.75, 1.5, -10]} />
        <group name="POI_LP1" position={[-4, 1.5, -10]} />
        <group name="POI_SAT" position={[0.5, 1.5, -5]} />
        <group name="CAM_HA" position={[5.206, 1.338, -5.697]} />
        <group name="CAM_SAT" position={[0.9, 0.5, -1.2]} />
        <group name="TAR_SA" position={[1.2, -0.085, -4.2]} />
        <group name="CAM_LP" position={[-4.374, 0.683, -10]} />
        <group name="CAM_DEFAULT" position={[-17.799, 7.407, 15.485]} />
        <group name="TAR_HA" position={[3, 0.141, -9.506]} />
        <group name="TAR_LP" position={[-2.75, 0.2, -8.6]} />
        <group name="TAR_DEFAULT" position={[0.946, 0.747, -10.776]} />
        <group name="TAR_RAF" position={[0.1, 0.05, -10.5]} />
        <group name="CAM_RAF" position={[-1.105, 0.683, -9.907]} />
        <group name="POI_RAF" position={[-1, 1.5, -10.0]} />
        <group name="POI_MET" position={[-1.625, 0, 6.14]} />
        <group name="TAR_HIS" position={[2.533, 0.066, -2.32]} />
        <group name="POI_HIS" position={[1, 1.5, -2.353]} />
        <group name="CAM_HIS" position={[4.564, 1.432, 0.801]} />
        <mesh
          name="Land001"
          castShadow
          receiveShadow
          geometry={nodes.Land001.geometry}
          material={materials.Land_1}
        />
        <mesh
          name="Land002"
          castShadow
          receiveShadow
          geometry={nodes.Land002.geometry}
          material={materials.Land_2}
        />
        <mesh
          name="Land003"
          castShadow
          receiveShadow
          geometry={nodes.Land003.geometry}
          material={materials.Land_3}
        />
        <mesh
          name="Land004"
          castShadow
          receiveShadow
          geometry={nodes.Land004.geometry}
          material={materials.Land_5}
        />
        <mesh
          name="Land005"
          castShadow
          receiveShadow
          geometry={nodes.Land005.geometry}
          material={materials.Land_4}
        />
        <mesh
          name="Land006"
          castShadow
          receiveShadow
          geometry={nodes.Land006.geometry}
          material={materials.Land_6}
        />
        <mesh
          name="Land007"
          castShadow
          receiveShadow
          geometry={nodes.Land007.geometry}
          material={materials.Land_7}
        />
        <mesh
          name="Land008"
          castShadow
          receiveShadow
          geometry={nodes.Land008.geometry}
          material={materials.Land_8}
        />
        <mesh
          name="Land009"
          castShadow
          receiveShadow
          geometry={nodes.Land009.geometry}
          material={materials.Land_9}
        />
        <mesh
          name="Land010"
          castShadow
          receiveShadow
          geometry={nodes.Land010.geometry}
          material={materials.Land_10}
        />
        <mesh
          name="Land011"
          castShadow
          receiveShadow
          geometry={nodes.Land011.geometry}
          material={materials.Land_11}
        />
        <mesh
          name="Land012"
          castShadow
          receiveShadow
          geometry={nodes.Land012.geometry}
          material={materials.Land_12}
        />
        <mesh
          name="Land013"
          castShadow
          receiveShadow
          geometry={nodes.Land013.geometry}
          material={materials.Land_13}
        />
        <instances.LSStructure
          name="LS_Structure"
          position={[-2.706, -0.044, -8.837]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Bucket
          name="Bucket"
          position={[-2.706, -0.044, -8.837]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.LSStruts
          name="LS_Struts"
          position={[-2.706, -0.044, -8.837]}
          rotation={[0, Math.PI / 2, 0]}
          scale={0.074}
        />
        <mesh
          name="LS_Rocket"
          castShadow
          receiveShadow
          geometry={nodes.LS_Rocket.geometry}
          material={materials.Rocket}
          position={[-2.706, -0.044, -8.837]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank050"
          position={[-1.591, -0.029, -8.931]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank049"
          position={[-1.591, 0, -8.931]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank051"
          position={[-1.223, -0.029, -9.016]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank056"
          position={[-1.223, 0, -9.016]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank057"
          position={[-1.223, -0.029, -8.949]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank058"
          position={[-1.223, 0, -8.949]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank059"
          position={[-1.223, -0.029, -8.883]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank060"
          position={[-1.223, 0, -8.883]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank061"
          position={[-1.223, -0.029, -8.742]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank062"
          position={[-1.223, 0, -8.742]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank063"
          position={[-1.223, -0.029, -8.675]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank064"
          position={[-1.223, 0, -8.675]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank065"
          position={[-1.223, -0.029, -8.607]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank066"
          position={[-1.223, 0, -8.607]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo022"
          position={[-2.706, -0.044, -8.837]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo018"
          position={[-2.673, -0.044, -8.727]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo020"
          position={[-2.7, -0.044, -8.642]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo021"
          position={[-1.808, -0.044, -8.785]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo041"
          position={[-1.756, -0.044, -9.136]}
          rotation={[0, Math.PI / 2, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo048"
          position={[-1.756, -0.044, -8.965]}
          rotation={[0, Math.PI / 2, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo067"
          position={[-1.214, -0.044, -8.552]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo047"
          position={[-1.87, -0.044, -8.638]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo068"
          position={[-1.504, -0.044, -8.602]}
          rotation={[0, -1.571, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo069"
          position={[-1.422, -0.044, -8.602]}
          rotation={[0, -1.571, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo010"
          position={[-2.898, -0.043, -7.472]}
          rotation={[Math.PI, -0.61, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo034"
          position={[-2.958, -0.043, -7.649]}
          rotation={[-Math.PI, 0.961, -Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo035"
          position={[-2.865, -0.043, -7.316]}
          rotation={[Math.PI, -0.61, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo036"
          position={[-2.475, -0.043, -7.598]}
          rotation={[0, 1.566, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo037"
          position={[-2.557, -0.043, -7.599]}
          rotation={[0, 1.566, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo029"
          position={[-2.475, -0.009, -7.598]}
          rotation={[0, 1.566, 0]}
          scale={0.074}
        />
        <mesh
          name="LS_Small_Pipes"
          castShadow
          receiveShadow
          geometry={nodes.LS_Small_Pipes.geometry}
          material={materials.Metal_Color}
          position={[-2.706, -0.044, -8.837]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.LSStairs
          name="LS_Stairs"
          position={[-2.706, -0.044, -8.837]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.LSRails
          name="LS_Rails"
          position={[-2.646, 0.14, -8.772]}
          rotation={[-Math.PI, 0, -Math.PI]}
          scale={[0.067, 0.074, 0.067]}
        />
        <instances.LSChonkyPipe
          name="LS_Chonky_Pipe"
          position={[-2.706, -0.044, -8.837]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <mesh
          name="LS_Foundation"
          castShadow
          receiveShadow
          geometry={nodes.LS_Foundation.geometry}
          material={materials.Ground}
          position={[-2.706, -0.044, -8.837]}
          rotation={[Math.PI, 0, Math.PI]}
          scale={0.074}
        />
        <instances.LSStructure
          name="LS_Structure001"
          position={[-0.972, 0.042, -13.098]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.LSStruts
          name="LS_Struts001"
          position={[-0.972, 0.042, -13.098]}
          rotation={[0, 0.773, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo002"
          position={[-0.836, 0.042, -13.184]}
          rotation={[0, 0.773, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo003"
          position={[-1.107, 0.042, -12.957]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo004"
          position={[-0.726, 0.042, -11.471]}
          rotation={[0, 0.773, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo005"
          position={[-0.518, 0.042, -11.183]}
          rotation={[0, -0.798, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo006"
          position={[-0.638, 0.042, -11.305]}
          rotation={[0, -0.798, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo007"
          position={[-0.134, 0.042, -11.831]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo008"
          position={[-0.785, 0.042, -11.619]}
          rotation={[0, 0.773, 0]}
          scale={0.074}
        />
        <instances.LSStairs
          name="LS_Stairs001"
          position={[-0.972, 0.042, -13.098]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.LSRails
          name="LS_Rails001"
          position={[-0.976, 0.227, -13.009]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={[0.067, 0.074, 0.067]}
        />
        <instances.LSChonkyPipe
          name="LS_Chonky_Pipe001"
          position={[-0.972, 0.042, -13.098]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.LSStructure
          name="LS_Structure002"
          position={[0.184, -0.093, -19.572]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.Bucket
          name="Bucket002"
          position={[0.184, -0.093, -19.572]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.LSStruts
          name="LS_Struts002"
          position={[0.184, -0.093, -19.572]}
          rotation={[0, 0.773, 0]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank015"
          position={[0.262, -0.093, -19.627]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank016"
          position={[0.262, -0.064, -19.627]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo012"
          position={[0.128, -0.093, -19.471]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo013"
          position={[0.049, -0.093, -19.431]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo014"
          position={[0.774, -0.093, -18.892]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo015"
          position={[1.062, -0.093, -19.1]}
          rotation={[0, 0.773, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo016"
          position={[0.939, -0.093, -18.981]}
          rotation={[0, 0.773, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo017"
          position={[1.022, -0.093, -18.305]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo019"
          position={[0.625, -0.093, -18.834]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo023"
          position={[0.855, -0.093, -18.547]}
          rotation={[Math.PI, -0.773, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo024"
          position={[0.912, -0.093, -18.488]}
          rotation={[Math.PI, -0.773, Math.PI]}
          scale={0.074}
        />
        <instances.LSStairs
          name="LS_Stairs002"
          position={[0.184, -0.093, -19.572]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.LSRails
          name="LS_Rails002"
          position={[0.179, 0.092, -19.483]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={[0.067, 0.074, 0.067]}
        />
        <instances.LSChonkyPipe
          name="LS_Chonky_Pipe002"
          position={[0.184, -0.093, -19.572]}
          rotation={[-Math.PI, 0.798, -Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo025"
          position={[0.158, -0.093, -18.196]}
          rotation={[Math.PI, -0.596, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo026"
          position={[0.204, -0.093, -18.129]}
          rotation={[Math.PI, -0.596, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo027"
          position={[0.024, -0.093, -18.394]}
          rotation={[Math.PI, -0.596, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo028"
          position={[2.057, -0.093, -19.607]}
          rotation={[0, -0.3, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo030"
          position={[1.954, -0.093, -19.451]}
          rotation={[Math.PI, -1.271, Math.PI]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo031"
          position={[2.159, -0.093, -19.729]}
          rotation={[0, -0.3, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo032"
          position={[1.82, -0.093, -19.872]}
          rotation={[0, 1.271, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo033"
          position={[1.742, -0.093, -19.896]}
          rotation={[0, 1.271, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo009"
          position={[1.954, -0.059, -19.451]}
          rotation={[Math.PI, -1.271, Math.PI]}
          scale={0.074}
        />
        <mesh
          name="Hanger_A"
          castShadow
          receiveShadow
          geometry={nodes.Hanger_A.geometry}
          material={materials.Hnager_A}
          position={[2.353, 0.036, -9.525]}
          rotation={[0, 0.267, 0]}
        />
        <instances.Tank
          name="Tank041"
          position={[2.458, -0.044, -10.361]}
          rotation={[0, -1.311, 0]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank042"
          position={[2.458, -0.015, -10.361]}
          rotation={[0, -1.311, 0]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank043"
          position={[2.405, -0.044, -10.347]}
          rotation={[0, -1.311, 0]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank044"
          position={[2.405, -0.015, -10.347]}
          rotation={[0, -1.311, 0]}
          scale={0.074}
        />
        <instances.Tank
          name="Tank045"
          position={[2.381, -0.044, -10.439]}
          rotation={[0, -1.311, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo038"
          position={[3.028, -0.057, -8.692]}
          rotation={[0, -1.309, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo039"
          position={[2.968, -0.057, -9.055]}
          rotation={[0, 0.262, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo040"
          position={[2.947, -0.057, -9.134]}
          rotation={[0, 0.262, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo042"
          position={[2.945, -0.057, -8.67]}
          rotation={[0, -1.309, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo043"
          position={[2.865, -0.057, -8.648]}
          rotation={[0, -1.309, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo044"
          position={[2.782, -0.057, -8.626]}
          rotation={[0, -1.309, 0]}
          scale={0.074}
        />
        <instances.Cargo
          name="Cargo045"
          position={[2.877, -0.057, -8.603]}
          rotation={[0, -1.309, 0]}
          scale={0.074}
        />
        <mesh
          name="TrackingRadar"
          castShadow
          receiveShadow
          geometry={nodes.TrackingRadar.geometry}
          material={materials['m_trackingRadar.001']}
          position={[1.992, -0.087, -3.452]}
          rotation={[-Math.PI, 0.02, -Math.PI]}
          scale={0.023}
        />
        <instances.RAFBunker
          name="RAF_Bunker000"
          position={[0.002, 0.034, -10.649]}
          rotation={[0, -0.302, 0]}
          scale={0.035}
        />
        <instances.RAFBunker
          name="RAF_Bunker001"
          position={[2.393, -0.083, -11.566]}
          rotation={[-0.035, -0.291, -0.01]}
          scale={0.035}
        />
        <instances.RAFSmallBuilding
          name="RAF_Small_Building000"
          position={[2.442, -0.096, -12.264]}
          rotation={[0, 1.318, 0]}
          scale={0.02}
        />
        <mesh
          name="RAF_Big_Building"
          castShadow
          receiveShadow
          geometry={nodes.RAF_Big_Building.geometry}
          material={materials.BigRAF}
          position={[2.057, -0.082, -5.671]}
          rotation={[Math.PI, -0.388, Math.PI]}
          scale={[0.03, 0.03, 0.045]}
        />
        <instances.RAFSmallBuilding
          name="RAF_Small_Building001"
          position={[1.628, -0.091, -5.474]}
          rotation={[-Math.PI, 1.169, -Math.PI]}
          scale={0.023}
        />
        <instances.RAFBunker
          name="RAF_Bunker002"
          position={[1.919, -0.094, -18.179]}
          rotation={[0, -1.057, 0]}
          scale={0.035}
        />
        <instances.RAFSmallBuilding
          name="RAF_Small_Building002"
          position={[0.854, -0.092, -17.608]}
          rotation={[0, 0.291, 0]}
          scale={0.02}
        />
        <mesh
          name="HIS_Round000"
          castShadow
          receiveShadow
          geometry={nodes.HIS_Round000.geometry}
          material={materials.BronzeAgeRuin}
          position={[2.231, -0.094, -19.785]}
          scale={0.02}
        />
        <instances.HISLong name="HIS_Long000" position={[-0.367, -0.091, -19.675]} scale={0.02} />
        <mesh
          name="HIS_Rect000"
          castShadow
          receiveShadow
          geometry={nodes.HIS_Rect000.geometry}
          material={materials.BronzeAgeRuin}
          position={[0.944, -0.091, -19.365]}
          rotation={[Math.PI, -1.404, Math.PI]}
          scale={0.02}
        />
        <instances.HISLong
          name="HIS_Long001"
          position={[3.529, -0.06, -9.277]}
          rotation={[0, 0.201, 0]}
          scale={0.02}
        />
        <mesh
          name="LS_Tower003"
          castShadow
          receiveShadow
          geometry={nodes.LS_Tower003.geometry}
          material={materials['Metal_Color.001']}
          position={[-2.706, -0.043, -8.837]}
          rotation={[-Math.PI, 0, -Math.PI]}
          scale={0.075}
        />
      </group>
    </group>
  )
}

useGLTF.preload(GLB_PATH)